// Data Bağlantısı Servisi
// Bu servis MongoDB ve Google Sheets bağlantılarını yönetir

import { User } from '../types';

export interface DataConnectionConfig {
  type: 'mongodb' | 'googlesheets';
  mongodb?: {
    connectionString: string;
    databaseName: string;
  };
  googlesheets?: {
    spreadsheetId: string;
    credentials: string;
  };
}


export interface Report {
  id: string;
  userId: string;
  type: string;
  title: string;
  content: any;
  createdAt: Date;
  updatedAt: Date;
}

export interface WorkTimeEntry {
  id: string;
  userId: string;
  type: 'work_start' | 'work_end' | 'break_start' | 'break_end';
  timestamp: Date;
  notes?: string;
  location?: string;
}

export interface ChatGroup {
  id: string;
  name: string;
  description?: string;
  adminId: string;
  members: string[];
  createdAt: Date;
  isActive: boolean;
}

export interface ChatMessage {
  id: string;
  groupId: string;
  userId: string;
  content: string;
  timestamp: Date;
  type: 'text' | 'system';
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  data?: any;
}

class DataConnectionService {
  private config: DataConnectionConfig | null = null;
  private mongoClient: any = null;
  private sheetsService: any = null;

  // Bağlantı yapılandırmasını ayarla
  setConfig(config: DataConnectionConfig) {
    this.config = config;
  }

  // MongoDB bağlantısını başlat
  private async initializeMongoDB() {
    if (!this.config?.mongodb) return false;
    
    try {
      // MongoDB driver'ı dinamik olarak yükle
      // eslint-disable-next-line no-eval
      const mongodb = await eval('import("mongodb")').catch(() => null);
      if (!mongodb) {
        console.warn('MongoDB paketi yüklü değil. Lütfen "npm install mongodb" komutunu çalıştırın.');
        return false;
      }
      const { MongoClient } = mongodb;
      this.mongoClient = new MongoClient(this.config.mongodb.connectionString);
      await this.mongoClient.connect();
      return true;
    } catch (error) {
      console.error('MongoDB bağlantı hatası:', error);
      return false;
    }
  }

  // Google Sheets servisini başlat
  private async initializeGoogleSheets() {
    if (!this.config?.googlesheets) return false;
    
    try {
      // Google Sheets API'yi dinamik olarak yükle
      // eslint-disable-next-line no-eval
      const googleapis = await eval('import("googleapis")').catch(() => null);
      if (!googleapis) {
        console.warn('Google APIs paketi yüklü değil. Lütfen "npm install googleapis" komutunu çalıştırın.');
        return false;
      }
      const { google } = googleapis;
      const credentials = JSON.parse(this.config.googlesheets.credentials);
      
      const auth = new google.auth.GoogleAuth({
        credentials,
        scopes: ['https://www.googleapis.com/auth/spreadsheets']
      });
      
      this.sheetsService = google.sheets({ version: 'v4', auth });
      return true;
    } catch (error) {
      console.error('Google Sheets bağlantı hatası:', error);
      return false;
    }
  }

  // MongoDB bağlantısını test et
  async testMongoDBConnection(connectionString: string, databaseName: string): Promise<ConnectionTestResult> {
    try {
      // Simüle edilmiş MongoDB bağlantı testi
      // Gerçek implementasyonda mongoose veya MongoDB driver kullanılacak
      
      // Connection string formatını kontrol et
      if (!connectionString.includes('mongodb://') && !connectionString.includes('mongodb+srv://')) {
        return {
          success: false,
          message: 'Geçersiz MongoDB connection string formatı'
        };
      }

      // Database adını kontrol et
      if (!databaseName || databaseName.trim().length === 0) {
        return {
          success: false,
          message: 'Veritabanı adı gerekli'
        };
      }

      // Simüle edilmiş bağlantı testi (2 saniye bekle)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Başarılı bağlantı simülasyonu
      return {
        success: true,
        message: 'MongoDB bağlantısı başarıyla kuruldu',
        data: {
          database: databaseName,
          collections: ['users', 'reports', 'chat_groups', 'chat_messages']
        }
      };
    } catch (error) {
      return {
        success: false,
        message: `MongoDB bağlantı hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Google Sheets bağlantısını test et
  async testGoogleSheetsConnection(spreadsheetId: string, credentials: string): Promise<ConnectionTestResult> {
    try {
      // Simüle edilmiş Google Sheets bağlantı testi
      // Gerçek implementasyonda Google Sheets API kullanılacak
      
      // Spreadsheet ID formatını kontrol et
      if (!spreadsheetId || spreadsheetId.trim().length === 0) {
        return {
          success: false,
          message: 'Google Sheets ID gerekli'
        };
      }

      // Credentials JSON formatını kontrol et
      try {
        const creds = JSON.parse(credentials);
        if (!creds.type || !creds.project_id) {
          return {
            success: false,
            message: 'Geçersiz service account JSON formatı'
          };
        }
      } catch {
        return {
          success: false,
          message: 'Service account JSON formatı geçersiz'
        };
      }

      // Simüle edilmiş bağlantı testi (2 saniye bekle)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Başarılı bağlantı simülasyonu
      return {
        success: true,
        message: 'Google Sheets bağlantısı başarıyla kuruldu',
        data: {
          spreadsheetId,
          sheets: ['Users', 'Reports', 'Chat_Groups', 'Chat_Messages']
        }
      };
    } catch (error) {
      return {
        success: false,
        message: `Google Sheets bağlantı hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Tek kullanıcı oluştur (admin hesap oluşturma için)
  async createUser(user: User, password: string): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) {
          return { success: false, message: 'MongoDB bağlantısı kurulamadı' };
        }

        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const usersCollection = db.collection('users');
        
        // Kullanıcıyı ekle
        await usersCollection.insertOne(user);
        
        // Şifreyi ayrı collection'da sakla
        const passwordsCollection = db.collection('user_passwords');
        await passwordsCollection.insertOne({
          userId: user.id,
          password: password, // Gerçek uygulamada hash'lenmeli
          createdAt: new Date()
        });
        
        return { success: true, message: 'Admin kullanıcısı oluşturuldu' };
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) {
          return { success: false, message: 'Google Sheets bağlantısı kurulamadı' };
        }

        const spreadsheetId = this.config.googlesheets!.spreadsheetId;
        
        // Users sayfasına kullanıcı ekle
        const userRow = [
          user.id,
          user.username,
          user.email,
          user.role,
          user.firstName,
          user.lastName,
          user.isActive ? 'true' : 'false',
          user.createdAt.toISOString(),
          user.updatedAt.toISOString(),
          user.permissions.join(','),
          user.companyName || ''
        ];
        
        await this.sheetsService.spreadsheets.values.append({
          spreadsheetId,
          range: 'Users!A:K',
          valueInputOption: 'RAW',
          requestBody: {
            values: [userRow]
          }
        });

        // Şifreyi ayrı sayfada sakla
        const passwordRow = [
          user.id,
          password, // Gerçek uygulamada hash'lenmeli
          new Date().toISOString()
        ];
        
        await this.sheetsService.spreadsheets.values.append({
          spreadsheetId,
          range: 'User_Passwords!A:C',
          valueInputOption: 'RAW',
          requestBody: {
            values: [passwordRow]
          }
        });
        
        return { success: true, message: 'Admin kullanıcısı oluşturuldu' };
      }
      
      return { success: false, message: 'Desteklenmeyen bağlantı türü' };
    } catch (error) {
      return {
        success: false,
        message: `Kullanıcı oluşturma hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Kullanıcıları kaydet
  async saveUsers(users: User[]): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) {
          return { success: false, message: 'MongoDB bağlantısı kurulamadı' };
        }

        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const collection = db.collection('users');
        
        // Mevcut kullanıcıları temizle ve yenilerini ekle
        await collection.deleteMany({});
        await collection.insertMany(users);
        
        return { success: true, message: 'Kullanıcılar MongoDB\'ye kaydedildi' };
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) {
          return { success: false, message: 'Google Sheets bağlantısı kurulamadı' };
        }

        // Users sayfasını temizle ve başlık satırını ekle
        await this.sheetsService.spreadsheets.values.clear({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: 'Users!A:Z'
        });

        // Başlık satırı
        await this.sheetsService.spreadsheets.values.update({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: 'Users!A1:H1',
          valueInputOption: 'RAW',
          requestBody: {
            values: [['ID', 'Username', 'Email', 'Role', 'Permissions', 'IsActive', 'CreatedAt', 'LastLogin']]
          }
        });

        // Kullanıcı verilerini ekle
        const userData = users.map(user => [
          user.id,
          user.username,
          user.email,
          user.role,
          user.permissions.join(','),
          user.isActive,
          user.createdAt.toISOString(),
          user.lastLogin?.toISOString() || ''
        ]);

        await this.sheetsService.spreadsheets.values.update({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: `Users!A2:H${userData.length + 1}`,
          valueInputOption: 'RAW',
          requestBody: { values: userData }
        });

        return { success: true, message: 'Kullanıcılar Google Sheets\'e kaydedildi' };
      }
      
      return { success: false, message: 'Desteklenmeyen veri bağlantısı türü' };
    } catch (error) {
      return {
        success: false,
        message: `Kullanıcı kaydetme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Kullanıcıları getir
  async getUsers(): Promise<User[]> {
    if (!this.config) return [];

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) return [];

        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const collection = db.collection('users');
        return await collection.find({}).toArray();
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) return [];

        const response = await this.sheetsService.spreadsheets.values.get({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: 'Users!A2:H'
        });

        const rows = response.data.values || [];
        return rows.map((row: any[]) => ({
          id: row[0],
          username: row[1],
          email: row[2],
          role: row[3],
          permissions: row[4] ? row[4].split(',') : [],
          isActive: row[5] === 'true',
          createdAt: new Date(row[6]),
          lastLogin: row[7] ? new Date(row[7]) : undefined
        }));
      }
    } catch (error) {
      console.error('Kullanıcı getirme hatası:', error);
    }
    
    return [];
  }

  // Raporları kaydet
  async saveReports(reports: any[]): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        console.log('MongoDB: Raporlar kaydediliyor...', reports);
        return { success: true, message: 'Raporlar MongoDB\'ye kaydedildi' };
      } else if (this.config.type === 'googlesheets') {
        console.log('Google Sheets: Raporlar kaydediliyor...', reports);
        return { success: true, message: 'Raporlar Google Sheets\'e kaydedildi' };
      }
      
      return { success: false, message: 'Desteklenmeyen veri bağlantısı türü' };
    } catch (error) {
      return {
        success: false,
        message: `Rapor kaydetme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Chat gruplarını kaydet
  async saveChatGroups(groups: any[]): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        console.log('MongoDB: Chat grupları kaydediliyor...', groups);
        return { success: true, message: 'Chat grupları MongoDB\'ye kaydedildi' };
      } else if (this.config.type === 'googlesheets') {
        console.log('Google Sheets: Chat grupları kaydediliyor...', groups);
        return { success: true, message: 'Chat grupları Google Sheets\'e kaydedildi' };
      }
      
      return { success: false, message: 'Desteklenmeyen veri bağlantısı türü' };
    } catch (error) {
      return {
        success: false,
        message: `Chat grup kaydetme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Chat mesajlarını kaydet
  async saveChatMessages(messages: ChatMessage[]): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) {
          return { success: false, message: 'MongoDB bağlantısı kurulamadı' };
        }

        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const collection = db.collection('chat_messages');
        
        // Mevcut mesajları temizle ve yenilerini ekle
        await collection.deleteMany({});
        await collection.insertMany(messages);
        
        return { success: true, message: 'Chat mesajları MongoDB\'ye kaydedildi' };
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) {
          return { success: false, message: 'Google Sheets bağlantısı kurulamadı' };
        }

        // Chat_Messages sayfasını temizle ve başlık satırını ekle
        await this.sheetsService.spreadsheets.values.clear({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: 'Chat_Messages!A:Z'
        });

        // Başlık satırı
        await this.sheetsService.spreadsheets.values.update({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: 'Chat_Messages!A1:F1',
          valueInputOption: 'RAW',
          requestBody: {
            values: [['ID', 'GroupID', 'UserID', 'Content', 'Timestamp', 'Type']]
          }
        });

        // Mesaj verilerini ekle
        const messageData = messages.map(message => [
          message.id,
          message.groupId,
          message.userId,
          message.content,
          message.timestamp.toISOString(),
          message.type
        ]);

        await this.sheetsService.spreadsheets.values.update({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: `Chat_Messages!A2:F${messageData.length + 1}`,
          valueInputOption: 'RAW',
          requestBody: { values: messageData }
        });

        return { success: true, message: 'Chat mesajları Google Sheets\'e kaydedildi' };
      }
      
      return { success: false, message: 'Desteklenmeyen veri bağlantısı türü' };
    } catch (error) {
      return {
        success: false,
        message: `Chat mesaj kaydetme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Mesai girişi kaydet
  async saveWorkTimeEntry(entry: WorkTimeEntry): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) {
          return { success: false, message: 'MongoDB bağlantısı kurulamadı' };
        }

        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const collection = db.collection('work_time_entries');
        await collection.insertOne(entry);
        
        return { success: true, message: 'Mesai girişi MongoDB\'ye kaydedildi' };
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) {
          return { success: false, message: 'Google Sheets bağlantısı kurulamadı' };
        }

        const entryData = [
          entry.id,
          entry.userId,
          entry.type,
          entry.timestamp.toISOString(),
          entry.notes || '',
          entry.location || ''
        ];

        await this.sheetsService.spreadsheets.values.append({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: 'WorkTime!A:F',
          valueInputOption: 'RAW',
          requestBody: { values: [entryData] }
        });

        return { success: true, message: 'Mesai girişi Google Sheets\'e kaydedildi' };
      }
      
      return { success: false, message: 'Desteklenmeyen veri bağlantısı türü' };
    } catch (error) {
      return {
        success: false,
        message: `Mesai girişi kaydetme hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Mesai geçmişini getir
  async getWorkTimeEntries(userId?: string): Promise<WorkTimeEntry[]> {
    if (!this.config) return [];

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) return [];

        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const collection = db.collection('work_time_entries');
        
        const query = userId ? { userId } : {};
        return await collection.find(query).sort({ timestamp: -1 }).toArray();
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) return [];

        const response = await this.sheetsService.spreadsheets.values.get({
          spreadsheetId: this.config.googlesheets!.spreadsheetId,
          range: 'WorkTime!A2:F'
        });

        const rows = response.data.values || [];
        let entries = rows.map((row: any[]) => ({
          id: row[0],
          userId: row[1],
          type: row[2],
          timestamp: new Date(row[3]),
          notes: row[4] || '',
          location: row[5] || ''
        }));

        if (userId) {
          entries = entries.filter((entry: WorkTimeEntry) => entry.userId === userId);
        }

        return entries.sort((a: WorkTimeEntry, b: WorkTimeEntry) => 
          b.timestamp.getTime() - a.timestamp.getTime()
        );
      }
    } catch (error) {
      console.error('Mesai geçmişi getirme hatası:', error);
    }
    
    return [];
  }

  // Tüm verileri senkronize et
  async syncAllData(data: {
    users: any[];
    reports: any[];
    chatGroups: any[];
    chatMessages: any[];
  }): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      console.log('Tüm veriler senkronize ediliyor...', this.config.type);
      
      // Tüm veri türlerini sırayla kaydet
      const results = await Promise.all([
        this.saveUsers(data.users),
        this.saveReports(data.reports),
        this.saveChatGroups(data.chatGroups),
        this.saveChatMessages(data.chatMessages)
      ]);

      const failedResults = results.filter(result => !result.success);
      
      if (failedResults.length > 0) {
        return {
          success: false,
          message: `Bazı veriler kaydedilemedi: ${failedResults.map(r => r.message).join(', ')}`
        };
      }

      return {
        success: true,
        message: 'Tüm veriler başarıyla senkronize edildi'
      };
    } catch (error) {
      return {
        success: false,
        message: `Veri senkronizasyon hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`
      };
    }
  }

  // Bağlantı durumunu kontrol et
  isConnected(): boolean {
    return this.config !== null;
  }

  // Bağlantı türünü al
  getConnectionType(): string | null {
    return this.config?.type || null;
  }

  // Kullanıcı aktivitesini logla
  async logUserActivity(userId: string, action: string, details?: any): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      const logEntry = {
        id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId,
        action,
        timestamp: new Date(),
        details: details || {},
        ipAddress: '127.0.0.1' // Gerçek uygulamada IP adresi alınabilir
      };

      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) { return { success: false, message: 'MongoDB bağlantısı kurulamadı' }; }
        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const logsCollection = db.collection('user_logs');
        await logsCollection.insertOne(logEntry);
        return { success: true, message: 'Log kaydı oluşturuldu' };
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) { return { success: false, message: 'Google Sheets bağlantısı kurulamadı' }; }
        const spreadsheetId = this.config.googlesheets!.spreadsheetId;
        const logRow = [
          logEntry.id,
          logEntry.userId,
          logEntry.action,
          logEntry.timestamp.toISOString(),
          JSON.stringify(logEntry.details),
          logEntry.ipAddress
        ];
        await this.sheetsService.spreadsheets.values.append({
          spreadsheetId,
          range: 'User_Logs!A:F',
          valueInputOption: 'RAW',
          requestBody: { values: [logRow] }
        });
        return { success: true, message: 'Log kaydı oluşturuldu' };
      }
      return { success: false, message: 'Desteklenmeyen bağlantı türü' };
    } catch (error) {
      return { success: false, message: `Log kaydı hatası: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}` };
    }
  }

  // Log verilerini çek
  async getLogs(): Promise<{ success: boolean; data?: any[]; message?: string }> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) { return { success: false, message: 'MongoDB bağlantısı kurulamadı' }; }
        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const logsCollection = db.collection('user_logs');
        const logs = await logsCollection.find({}).sort({ timestamp: -1 }).toArray();
        return { success: true, data: logs };
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) { return { success: false, message: 'Google Sheets bağlantısı kurulamadı' }; }
        const spreadsheetId = this.config.googlesheets!.spreadsheetId;
        const response = await this.sheetsService.spreadsheets.values.get({
          spreadsheetId,
          range: 'User_Logs!A:F'
        });
        const rows = response.data.values || [];
        const logs = rows.slice(1).map((row: any[]) => ({
          id: row[0],
          userId: row[1],
          action: row[2],
          timestamp: new Date(row[3]),
          details: JSON.parse(row[4] || '{}'),
          ipAddress: row[5]
        }));
        return { success: true, data: logs };
      }
      return { success: false, message: 'Desteklenmeyen bağlantı türü' };
    } catch (error) {
      return { success: false, message: `Log verileri alınamadı: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}` };
    }
  }

  // Panel ayarlarını kaydet
  async savePanelSettings(settings: any): Promise<ConnectionTestResult> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) { return { success: false, message: 'MongoDB bağlantısı kurulamadı' }; }
        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const settingsCollection = db.collection('panel_settings');
        await settingsCollection.replaceOne(
          { type: 'panel' },
          { type: 'panel', ...settings, updatedAt: new Date() },
          { upsert: true }
        );
        return { success: true, message: 'Panel ayarları kaydedildi' };
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) { return { success: false, message: 'Google Sheets bağlantısı kurulamadı' }; }
        const spreadsheetId = this.config.googlesheets!.spreadsheetId;
        
        // Panel ayarları için sheet oluştur veya güncelle
        const settingsRow = [
          'panel',
          settings.url || '',
          settings.title || 'Panel',
          settings.allowNavigation || false,
          new Date().toISOString()
        ];
        
        await this.sheetsService.spreadsheets.values.update({
          spreadsheetId,
          range: 'Panel_Settings!A:E',
          valueInputOption: 'RAW',
          requestBody: { values: [settingsRow] }
        });
        return { success: true, message: 'Panel ayarları kaydedildi' };
      }
      return { success: false, message: 'Desteklenmeyen bağlantı türü' };
    } catch (error) {
      return { success: false, message: `Panel ayarları kaydedilemedi: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}` };
    }
  }

  // Panel ayarlarını yükle
  async getPanelSettings(): Promise<{ success: boolean; data?: any; message?: string }> {
    if (!this.config) {
      return { success: false, message: 'Veri bağlantısı yapılandırılmamış' };
    }

    try {
      if (this.config.type === 'mongodb') {
        await this.initializeMongoDB();
        if (!this.mongoClient) { return { success: false, message: 'MongoDB bağlantısı kurulamadı' }; }
        const db = this.mongoClient.db(this.config.mongodb!.databaseName);
        const settingsCollection = db.collection('panel_settings');
        const settings = await settingsCollection.findOne({ type: 'panel' });
        return { success: true, data: settings };
      } else if (this.config.type === 'googlesheets') {
        await this.initializeGoogleSheets();
        if (!this.sheetsService) { return { success: false, message: 'Google Sheets bağlantısı kurulamadı' }; }
        const spreadsheetId = this.config.googlesheets!.spreadsheetId;
        
        try {
          const response = await this.sheetsService.spreadsheets.values.get({
            spreadsheetId,
            range: 'Panel_Settings!A:E'
          });
          const rows = response.data.values || [];
          if (rows.length > 1) {
            const row = rows[1]; // İlk satır başlık
            const settings = {
              type: row[0],
              url: row[1],
              title: row[2],
              allowNavigation: row[3] === 'TRUE',
              updatedAt: row[4]
            };
            return { success: true, data: settings };
          } else {
            return { success: false, message: 'Panel ayarları bulunamadı' };
          }
        } catch (error) {
          // Sheet yoksa oluştur
          await this.sheetsService.spreadsheets.batchUpdate({
            spreadsheetId,
            requestBody: {
              requests: [{
                addSheet: {
                  properties: {
                    title: 'Panel_Settings'
                  }
                }
              }]
            }
          });
          
          // Başlık satırı ekle
          await this.sheetsService.spreadsheets.values.update({
            spreadsheetId,
            range: 'Panel_Settings!A1:E1',
            valueInputOption: 'RAW',
            requestBody: { values: [['type', 'url', 'title', 'allowNavigation', 'updatedAt']] }
          });
          
          return { success: false, message: 'Panel ayarları bulunamadı' };
        }
      }
      return { success: false, message: 'Desteklenmeyen bağlantı türü' };
    } catch (error) {
      return { success: false, message: `Panel ayarları alınamadı: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}` };
    }
  }
}

// Singleton instance
const dataConnectionService = new DataConnectionService();
export default dataConnectionService;

